// ═══════════════════════════════════════════════════════════════════════════
// Shift Types, Segment Types & Pay Types — BuildMyShifts
// Defines constants and helpers for complex shift structures used in
// care, supported living, hospitality, security, and similar sectors.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Segment types for multi-part shifts
 */
export const SEGMENT_TYPES = {
  STANDARD: 'standard',       // Normal hourly-rate shift
  SLEEP_IN: 'sleepIn',        // Flat-rate sleep-in period
  ON_CALL: 'onCall',          // Standby — low rate, higher if called out
  BREAK: 'break',             // Unpaid break segment
};

/**
 * Shift block types — how segments relate
 */
export const BLOCK_TYPES = {
  SINGLE: 'single',           // A regular single-segment shift (backward compatible)
  LINKED: 'linked',           // Multi-part shift block (e.g. day + sleep-in + morning)
  SPLIT: 'split',             // Two separate working periods in one day
};

/**
 * Pay enhancement categories
 */
export const PAY_TYPES = {
  STANDARD: 'standard',
  OVERTIME: 'overtime',
  NIGHT_PREMIUM: 'nightPremium',
  WEEKEND: 'weekend',
  BANK_HOLIDAY: 'bankHoliday',
  SLEEP_IN_FLAT: 'sleepInFlat',
  SLEEP_IN_DISTURBANCE: 'sleepInDisturbance',
  ON_CALL_STANDBY: 'onCallStandby',
  ON_CALL_CALLOUT: 'onCallCallout',
};

/**
 * UK Bank Holidays — a simplified static list; in production these would be
 * fetched from gov.uk API or configurable per organisation.
 */
export const UK_BANK_HOLIDAYS_2025_2026 = [
  '2025-01-01', '2025-04-18', '2025-04-21', '2025-05-05', '2025-05-26',
  '2025-08-25', '2025-12-25', '2025-12-26',
  '2026-01-01', '2026-04-03', '2026-04-06', '2026-05-04', '2026-05-25',
  '2026-08-31', '2026-12-25', '2026-12-28',
];

/**
 * Industry sectors with default configurations
 */
export const SECTOR_PRESETS = {
  general: {
    label: 'General',
    description: 'Default settings for most organisations',
    defaultShiftDuration: 8,
    allowSleepIns: false,
    allowOnCall: false,
    sleepInCountsAsWorkingTime: false,
    defaultPayMultipliers: {
      standard: 1.0,
      overtime: 1.5,
      nightPremium: 1.25,
      weekend: 1.25,
      bankHoliday: 2.0,
    },
  },
  care: {
    label: 'Care / Supported Living',
    description: 'Residential care homes, supported living, domiciliary care',
    defaultShiftDuration: 8,
    allowSleepIns: true,
    allowOnCall: true,
    sleepInCountsAsWorkingTime: false,
    defaultPayMultipliers: {
      standard: 1.0,
      overtime: 1.5,
      nightPremium: 1.33,
      weekend: 1.25,
      bankHoliday: 2.0,
    },
    defaultSleepInRate: 50.00,
    defaultDisturbanceRate: 15.00,
  },
  hospitality: {
    label: 'Hospitality',
    description: 'Hotels, restaurants, bars, events',
    defaultShiftDuration: 8,
    allowSleepIns: false,
    allowOnCall: false,
    sleepInCountsAsWorkingTime: false,
    defaultPayMultipliers: {
      standard: 1.0,
      overtime: 1.5,
      nightPremium: 1.2,
      weekend: 1.0,
      bankHoliday: 1.5,
    },
  },
  security: {
    label: 'Security',
    description: 'Security guards, surveillance, patrol services',
    defaultShiftDuration: 12,
    allowSleepIns: true,
    allowOnCall: true,
    sleepInCountsAsWorkingTime: true,
    defaultPayMultipliers: {
      standard: 1.0,
      overtime: 1.5,
      nightPremium: 1.3,
      weekend: 1.25,
      bankHoliday: 2.0,
    },
    defaultSleepInRate: 40.00,
    defaultDisturbanceRate: 12.00,
  },
  healthcare: {
    label: 'Healthcare / NHS',
    description: 'Hospitals, clinics, NHS trusts, GP surgeries',
    defaultShiftDuration: 12,
    allowSleepIns: true,
    allowOnCall: true,
    sleepInCountsAsWorkingTime: false,
    defaultPayMultipliers: {
      standard: 1.0,
      overtime: 1.5,
      nightPremium: 1.37,
      weekend: 1.44,
      bankHoliday: 2.0,
    },
    defaultSleepInRate: 55.00,
    defaultDisturbanceRate: 20.00,
  },
  retail: {
    label: 'Retail',
    description: 'Shops, warehouses, e-commerce fulfilment',
    defaultShiftDuration: 8,
    allowSleepIns: false,
    allowOnCall: false,
    sleepInCountsAsWorkingTime: false,
    defaultPayMultipliers: {
      standard: 1.0,
      overtime: 1.5,
      nightPremium: 1.25,
      weekend: 1.0,
      bankHoliday: 1.5,
    },
  },
};

/**
 * Default pay rules configuration stored in company settings
 */
export const DEFAULT_PAY_RULES = {
  sector: 'general',
  currency: 'GBP',
  sleepInDisplayMode: 'hours',
  currencySymbol: '£',
  // Pay multipliers
  overtimeMultiplier: 1.5,
  nightPremiumMultiplier: 1.25,
  weekendMultiplier: 1.25,
  bankHolidayMultiplier: 2.0,
  // Night premium hours (auto-detect)
  nightStartHour: 23,   // 11 PM
  nightEndHour: 6,       // 6 AM
  // Weekend definition
  weekendDays: [0, 6],   // Sunday=0, Saturday=6
  // Sleep-in
  sleepInFlatRate: 50.00,
  sleepInDisturbanceRate: 15.00,
  sleepInCountsAsWorkingTime: false,
  // On-call
  onCallStandbyRate: 5.00,  // per hour
  onCallCalloutRate: 15.00, // per hour if called out
  // Overtime thresholds
  dailyOvertimeThresholdHours: 8,
  weeklyOvertimeThresholdHours: 40,
  // Custom bank holidays (override built-in list)
  customBankHolidays: [],
  // Role-based pay rates (optional — overrides pay per role)
  rolePayRates: {},
};

/**
 * Create a new shift segment object
 */
export function createSegment({
  type = SEGMENT_TYPES.STANDARD,
  startTime = '09:00',
  endTime = '17:00',
  payType = PAY_TYPES.STANDARD,
  flatRate = null,
  notes = '',
} = {}) {
  return {
    id: `seg_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    type,
    startTime,
    endTime,
    payType,
    flatRate,       // null means hourly; a number means flat fee (e.g. sleep-in £50)
    notes,
  };
}

/**
 * Create a new shift block (can contain 1+ segments)
 */
export function createShiftBlock({
  blockType = BLOCK_TYPES.SINGLE,
  date = '',
  segments = [],
  assignedStaffId = null,
  assignedStaffName = '',
  groupId = '',
  groupName = '',
  groupColor = '',
  requiredRole = '',
  requiredSkills = [],
  templateId = null,
  templateName = '',
  notes = '',
} = {}) {
  // Auto-detect block type from segments
  let detectedType = blockType;
  if (segments.length > 1) {
    detectedType = BLOCK_TYPES.LINKED;
  }

  return {
    blockType: detectedType,
    date,
    segments,
    assignedStaffId,
    assignedStaffName,
    groupId,
    groupName,
    groupColor,
    requiredRole,
    requiredSkills,
    templateId,
    templateName,
    notes,
    // Computed fields (set before saving)
    startTime: segments[0]?.startTime || '09:00',
    endTime: segments[segments.length - 1]?.endTime || '17:00',
    hasSleepIn: segments.some(s => s.type === SEGMENT_TYPES.SLEEP_IN),
    hasOnCall: segments.some(s => s.type === SEGMENT_TYPES.ON_CALL),
  };
}

/**
 * Create a reusable shift template
 */
export function createShiftTemplate({
  name = '',
  description = '',
  sector = 'general',
  segments = [],
  requiredRole = '',
  requiredSkills = [],
  tags = [],
} = {}) {
  return {
    id: `tpl_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    name,
    description,
    sector,
    segments,
    requiredRole,
    requiredSkills,
    tags,
    usageCount: 0,
    lastUsed: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Predefined shift templates for common sector patterns
 */
export const BUILT_IN_TEMPLATES = [
  {
    id: 'tpl_care_24hr',
    name: 'Care 24hr Block',
    description: 'Day shift + sleep-in + morning handover',
    sector: 'care',
    segments: [
      { type: SEGMENT_TYPES.STANDARD, startTime: '14:00', endTime: '22:00', payType: PAY_TYPES.STANDARD, flatRate: null, notes: 'Day shift' },
      { type: SEGMENT_TYPES.SLEEP_IN, startTime: '22:00', endTime: '07:00', payType: PAY_TYPES.SLEEP_IN_FLAT, flatRate: 50, notes: 'Sleep-in' },
      { type: SEGMENT_TYPES.STANDARD, startTime: '07:00', endTime: '10:00', payType: PAY_TYPES.STANDARD, flatRate: null, notes: 'Morning handover' },
    ],
    requiredRole: 'Care Worker',
    tags: ['care', '24hr', 'sleep-in'],
  },
  {
    id: 'tpl_care_waking_night',
    name: 'Waking Night',
    description: 'Overnight waking shift with night premium',
    sector: 'care',
    segments: [
      { type: SEGMENT_TYPES.STANDARD, startTime: '20:00', endTime: '08:00', payType: PAY_TYPES.NIGHT_PREMIUM, flatRate: null, notes: 'Waking night' },
    ],
    requiredRole: 'Care Worker',
    tags: ['care', 'night', 'waking'],
  },
  {
    id: 'tpl_security_12hr_night',
    name: 'Security Night Shift',
    description: '12-hour overnight security patrol',
    sector: 'security',
    segments: [
      { type: SEGMENT_TYPES.STANDARD, startTime: '19:00', endTime: '07:00', payType: PAY_TYPES.NIGHT_PREMIUM, flatRate: null, notes: 'Night patrol' },
    ],
    requiredRole: 'Security Officer',
    tags: ['security', 'night', '12hr'],
  },
  {
    id: 'tpl_security_oncall',
    name: 'Security On-Call',
    description: 'On-call standby with potential callout',
    sector: 'security',
    segments: [
      { type: SEGMENT_TYPES.ON_CALL, startTime: '18:00', endTime: '06:00', payType: PAY_TYPES.ON_CALL_STANDBY, flatRate: null, notes: 'On-call standby' },
    ],
    requiredRole: 'Security Officer',
    tags: ['security', 'on-call'],
  },
  {
    id: 'tpl_hospitality_split',
    name: 'Hospitality Split Shift',
    description: 'Lunch service + evening service with break',
    sector: 'hospitality',
    segments: [
      { type: SEGMENT_TYPES.STANDARD, startTime: '11:00', endTime: '15:00', payType: PAY_TYPES.STANDARD, flatRate: null, notes: 'Lunch service' },
      { type: SEGMENT_TYPES.BREAK, startTime: '15:00', endTime: '17:00', payType: PAY_TYPES.STANDARD, flatRate: null, notes: 'Break' },
      { type: SEGMENT_TYPES.STANDARD, startTime: '17:00', endTime: '23:00', payType: PAY_TYPES.STANDARD, flatRate: null, notes: 'Evening service' },
    ],
    requiredRole: '',
    tags: ['hospitality', 'split', 'restaurant'],
  },
  {
    id: 'tpl_healthcare_long_day',
    name: 'Healthcare Long Day',
    description: '12-hour hospital day shift',
    sector: 'healthcare',
    segments: [
      { type: SEGMENT_TYPES.STANDARD, startTime: '07:00', endTime: '19:00', payType: PAY_TYPES.STANDARD, flatRate: null, notes: 'Long day' },
    ],
    requiredRole: 'Nurse',
    tags: ['healthcare', '12hr', 'day'],
  },
  {
    id: 'tpl_healthcare_oncall_night',
    name: 'Healthcare On-Call Night',
    description: 'On-call overnight with potential callout',
    sector: 'healthcare',
    segments: [
      { type: SEGMENT_TYPES.ON_CALL, startTime: '20:00', endTime: '08:00', payType: PAY_TYPES.ON_CALL_STANDBY, flatRate: null, notes: 'On-call overnight' },
    ],
    requiredRole: 'Doctor',
    tags: ['healthcare', 'on-call', 'night'],
  },
];

/**
 * Get total working hours for a shift block (excluding breaks and optionally sleep-ins)
 */
export function getBlockWorkingHours(segments, includeSleepIn = false) {
  let totalMinutes = 0;
  for (const seg of segments) {
    if (seg.type === SEGMENT_TYPES.BREAK) continue;
    if (seg.type === SEGMENT_TYPES.SLEEP_IN && !includeSleepIn) continue;

    const [sh, sm] = seg.startTime.split(':').map(Number);
    const [eh, em] = seg.endTime.split(':').map(Number);
    let startMins = sh * 60 + (sm || 0);
    let endMins = eh * 60 + (em || 0);
    if (endMins <= startMins) endMins += 24 * 60; // Overnight
    totalMinutes += endMins - startMins;
  }
  return totalMinutes / 60;
}

/**
 * Get the overall time span of a shift block (first start to last end)
 */
export function getBlockTimeSpan(segments) {
  if (!segments || segments.length === 0) return { startTime: '00:00', endTime: '00:00', spanHours: 0 };
  const firstStart = segments[0].startTime;
  const lastEnd = segments[segments.length - 1].endTime;

  const [sh, sm] = firstStart.split(':').map(Number);
  const [eh, em] = lastEnd.split(':').map(Number);
  let startMins = sh * 60 + (sm || 0);
  let endMins = eh * 60 + (em || 0);
  if (endMins <= startMins) endMins += 24 * 60;

  return {
    startTime: firstStart,
    endTime: lastEnd,
    spanHours: (endMins - startMins) / 60,
  };
}

/**
 * Validate segment continuity — segments must be consecutive with no gaps (for linked blocks)
 */
export function validateSegmentContinuity(segments) {
  if (segments.length <= 1) return { valid: true, errors: [] };
  const errors = [];

  for (let i = 1; i < segments.length; i++) {
    const prevEnd = segments[i - 1].endTime;
    const currStart = segments[i].startTime;
    if (prevEnd !== currStart) {
      errors.push(`Gap between segment ${i} ending at ${prevEnd} and segment ${i + 1} starting at ${currStart}`);
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Check if a date is a bank holiday
 */
export function isBankHoliday(dateStr, customHolidays = []) {
  const allHolidays = [...UK_BANK_HOLIDAYS_2025_2026, ...customHolidays];
  return allHolidays.includes(dateStr);
}

/**
 * Check if a date is a weekend
 */
export function isWeekend(dateStr, weekendDays = [0, 6]) {
  const day = new Date(dateStr + 'T12:00:00').getDay();
  return weekendDays.includes(day);
}

/**
 * Determine the effective pay type for a segment based on date/time rules
 */
export function detectPayType(segment, dateStr, payRules = DEFAULT_PAY_RULES) {
  // Explicit flat-rate types take precedence
  if (segment.type === SEGMENT_TYPES.SLEEP_IN) return PAY_TYPES.SLEEP_IN_FLAT;
  if (segment.type === SEGMENT_TYPES.ON_CALL) return PAY_TYPES.ON_CALL_STANDBY;
  if (segment.type === SEGMENT_TYPES.BREAK) return PAY_TYPES.STANDARD;

  // Bank holiday overrides everything
  if (isBankHoliday(dateStr, payRules.customBankHolidays)) return PAY_TYPES.BANK_HOLIDAY;

  // Weekend override
  if (isWeekend(dateStr, payRules.weekendDays)) return PAY_TYPES.WEEKEND;

  // Night premium detection
  const [sh] = segment.startTime.split(':').map(Number);
  const [eh] = segment.endTime.split(':').map(Number);
  const nightStart = payRules.nightStartHour || 23;
  const nightEnd = payRules.nightEndHour || 6;

  // If the shift starts or ends within night hours
  if (sh >= nightStart || sh < nightEnd || eh >= nightStart || eh < nightEnd || eh < sh) {
    return PAY_TYPES.NIGHT_PREMIUM;
  }

  return PAY_TYPES.STANDARD;
}

/**
 * Get the pay multiplier for a given pay type
 */
export function getPayMultiplier(payType, payRules = DEFAULT_PAY_RULES) {
  switch (payType) {
    case PAY_TYPES.OVERTIME: return payRules.overtimeMultiplier || 1.5;
    case PAY_TYPES.NIGHT_PREMIUM: return payRules.nightPremiumMultiplier || 1.25;
    case PAY_TYPES.WEEKEND: return payRules.weekendMultiplier || 1.25;
    case PAY_TYPES.BANK_HOLIDAY: return payRules.bankHolidayMultiplier || 2.0;
    default: return 1.0;
  }
}

/**
 * Convert a legacy single-shift document to the new block/segment format.
 * Backward compatible — old shifts become single-segment blocks.
 */
export function legacyShiftToBlock(shift) {
  if (shift.segments && shift.segments.length > 0) return shift; // Already new format

  return {
    ...shift,
    blockType: BLOCK_TYPES.SINGLE,
    segments: [
      {
        id: `seg_legacy_${shift.id}`,
        type: SEGMENT_TYPES.STANDARD,
        startTime: shift.startTime,
        endTime: shift.endTime,
        payType: PAY_TYPES.STANDARD,
        flatRate: null,
        notes: '',
      },
    ],
    hasSleepIn: false,
    hasOnCall: false,
  };
}

const shiftTypes = {
  SEGMENT_TYPES,
  BLOCK_TYPES,
  PAY_TYPES,
  UK_BANK_HOLIDAYS_2025_2026,
  SECTOR_PRESETS,
  DEFAULT_PAY_RULES,
  BUILT_IN_TEMPLATES,
  createSegment,
  createShiftBlock,
  createShiftTemplate,
  getBlockWorkingHours,
  getBlockTimeSpan,
  validateSegmentContinuity,
  isBankHoliday,
  isWeekend,
  detectPayType,
  getPayMultiplier,
  legacyShiftToBlock,
};
export default shiftTypes;
